import { ReactElement, useState } from 'react';
import { TPoint } from 'src/app/project/modules/points/points.model';
import { EPriority } from 'src/app/project/modules/points/priorities';
import { EStatus } from 'src/app/project/shared/enums/status.enum';
import { TPointCustomField } from 'src/app/project/view-models/custom-field-response-model';
import { EFloatingActionsOptions } from 'src/app/project/modules/site/site-options/site-options-more-floating-actions/floating-actions-options.const';
import BulkAssigneesDialog from '../molecules/BulkAssigneesDialog';
import BulkCustomFieldsDialog, {
  TBulkCustomFieldsDialogInputs,
} from '../molecules/BulkCustomFieldsDialog';
import BulkEditMenu, { TBulkEditMenuProps } from '../molecules/BulkEditMenu';
import BulkFlagDialog from '../molecules/BulkFlagDialog';
import BulkPriorityDialog from '../molecules/BulkPriorityDialog';
import BulkStatusDialog from '../molecules/BulkStatusDialog';
import BulkTagsDialog from '../molecules/BulkTagsDialog';
import { TPointAssignee } from '../molecules/PointAssignees';

export enum EBulkEditEventType {
  FLAGGED = 'FLAGGED',
  PRIORITY = 'PRIORITY',
  STATUS = 'STATUS',
  CUSTOM_FIELDS = 'CF',
  ASSIGNEES = 'ASSIGNEES',
  TAGS = 'TAGS',
}

export type TBulkPointChanges = Partial<TPoint> & {
  customFieldsList?: TPointCustomField[];
};

export type TBulkEditCustomFields = TBulkCustomFieldsDialogInputs & {
  onOpen: () => void;
};

export type TBulkEditProps = Omit<TBulkEditMenuProps, 'onSelect'> & {
  isFlagged: boolean;
  customFields: TBulkEditCustomFields;
  assigneeOptions: TPointAssignee[];
  commonTags: string[];
  tagOptions: string[];
  onSave: (
    changes: TBulkPointChanges,
    eventType: EBulkEditEventType,
    mentions?: string[],
  ) => Promise<void>;
};

const BulkEdit = ({
  isFlagged,
  customFields,
  assigneeOptions,
  commonTags,
  tagOptions,
  onSave,
  ...menuProps
}: TBulkEditProps): ReactElement => {
  const [openDialog, setOpenDialog] = useState<EFloatingActionsOptions | undefined>(undefined);
  const { onOpen: onOpenCustomFields, ...customFieldsInputs } = customFields;

  const handleSelect = (option: EFloatingActionsOptions): void => {
    if (option === EFloatingActionsOptions.CUSTOM_FIELDS) {
      onOpenCustomFields();
    }

    setOpenDialog(option);
  };

  const closeDialog = (): void => {
    setOpenDialog(undefined);
  };

  return (
    <>
      <BulkEditMenu {...menuProps} onSelect={handleSelect} />
      {openDialog === EFloatingActionsOptions.FLAG ? (
        <BulkFlagDialog
          isFlagged={isFlagged}
          onClose={closeDialog}
          onSave={(flagged): Promise<void> => onSave({ flagged }, EBulkEditEventType.FLAGGED)}
        />
      ) : null}
      {openDialog === EFloatingActionsOptions.PRIORITY ? (
        <BulkPriorityDialog
          onClose={closeDialog}
          onSave={(priority: EPriority): Promise<void> =>
            onSave({ priority }, EBulkEditEventType.PRIORITY)
          }
        />
      ) : null}
      {openDialog === EFloatingActionsOptions.STATUS ? (
        <BulkStatusDialog
          onClose={closeDialog}
          onSave={(status: EStatus): Promise<void> => onSave({ status }, EBulkEditEventType.STATUS)}
        />
      ) : null}
      {openDialog === EFloatingActionsOptions.CUSTOM_FIELDS ? (
        <BulkCustomFieldsDialog
          {...customFieldsInputs}
          onClose={closeDialog}
          onSave={(customFieldsList, mentionIds): Promise<void> =>
            onSave({ customFieldsList }, EBulkEditEventType.CUSTOM_FIELDS, mentionIds)
          }
        />
      ) : null}
      {openDialog === EFloatingActionsOptions.TAGS ? (
        <BulkTagsDialog
          commonTags={commonTags}
          options={tagOptions}
          onClose={closeDialog}
          onSave={(tags): Promise<void> => onSave({ tags }, EBulkEditEventType.TAGS)}
        />
      ) : null}
      {openDialog === EFloatingActionsOptions.ASSIGNEES ? (
        <BulkAssigneesDialog
          options={assigneeOptions}
          onClose={closeDialog}
          onSave={(assignees): Promise<void> => onSave({ assignees }, EBulkEditEventType.ASSIGNEES)}
        />
      ) : null}
    </>
  );
};

export default BulkEdit;
